import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { strings } from '/imports/strings';
import { Checkbox } from '/imports/ui/Checkbox';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { toast } from 'react-toastify';
import { Page } from '/imports/ui/Page';
import { Button } from '/imports/ui/Button';
import { toastOnError } from '/imports/ui/utils';
import { modelClass, UtilityAccount, clearAutomationProfile } from '/imports/api/utilityAccounts';
import { modelClass as utilityBillClass } from '/imports/api/utilityBills';
import { modelClass as consumptionMeterClass } from '/imports/api/consumptionMeters';
import { Input } from '/imports/ui/Input';
import { formatCurrency, formatDate, formatDateTime, formatNumber } from '/imports/utils';
import { FormField } from '/imports/ui/FormField';
import { ListMethodCatalog } from '/imports/ui/ListMethodCatalog';
import { Icon } from '/imports/ui/Icon';
import { DateInput } from '/imports/ui/DateInput';
import { DividerTabs } from '/imports/ui/DividerTabs';

const UtilityAccountPageStyled = styled(Page)`
  .catalog {
    max-height: 45rem;
    margin-top: 1rem;
    .row-select, .row-actions {
      display: none;
    }
  }
`;

/** Formats a date-only string at noon UTC to avoid timezone day-shifts. */
const formattedDate = (date: string) => formatDate(new Date(`${date}T12:00:00.000Z`));

/**
 * Super-user form page for editing a utility account with bills and meters tabs.
 */
export const UtilityAccountPage = () => {
  const { _id } = useParams<any>();
  const defaultUpdate: Partial<UtilityAccount> = { _id, active: true };
  const [update, setUpdate] = useState<Partial<UtilityAccount>>(defaultUpdate);
  const documentRef = useRef<UtilityAccount>(undefined);
  const doc = documentRef.current;
  const [activeTab, setActiveTab] = useState('bills');
  const clearProfile = toastOnError(async () => {
    if (!_id) return;
    await clearAutomationProfile.call(_id);
    toast.success(strings.AutomationProfileCleared());
  });
  return <ModelFormPage
    modelClass={modelClass}
    styled={UtilityAccountPageStyled}
    permission={(user) => user?.isSuperUser}
    documentRef={documentRef}
    defaultUpdate={defaultUpdate}
    update={update}
    setUpdate={setUpdate}
    formSpec={[
      [{ field: 'active', input: { type: Checkbox } }],
      [
        { field: 'name', input: { type: Input, props: { placeholder: doc?.username } } },
        { field: 'username', input: { type: Input, props: { disabled: true, autoComplete: 'off' } } },
        { field: 'password', input: (setUpdate, update) => (
          <FormField key="password" field="password">
            <label>{strings.Password()}</label>
            <Input type="password" autoComplete="off" placeholder="••••••••" value={update.password || ''} onChange={(password) => setUpdate({ ...update, password })} />
          </FormField>
        ) },
      ],
      [
        { field: 'type', input: { type: Input, props: { disabled: true } } },
        {
          field: 'expireAt', input: () => (
            <FormField key="expireAt" field="expireAt">
              <label>{strings.Expires()}</label>
              <DateInput value={doc?.expireAt || update.expireAt} />
            </FormField>
          )
        },
        {
          field: 'createdAt', input: () => (
            <FormField key="createdAt" field="createdAt"><label>{strings.CreatedAt()}</label><Input placeholder={formatDateTime(doc?.createdAt)} disabled /></FormField>
          )
        }
      ],
    ]}
    extraButtons={
      <>
        {_id && <Button className="secondary" tip={strings.ClearAutomationProfileTooltip()} onClick={() => clearProfile()}>{strings.ClearAutomationProfile()}</Button>}
      </>
    }
    pageFooter={
      <>
        <div>
          <DividerTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={[
              { id: 'bills', label: strings.UtilityBills() },
              { id: 'meters', label: strings.ConsumptionMeters() },
            ]}
          />
          {
            activeTab === 'bills' ?
              <ListMethodCatalog
                listMethod={utilityBillClass.listMethod}
                columns={[
                  { field: 'sourceId', label: 'ID' },
                  { field: 'pdf', label: 'PDF', render: (doc) => <a href={doc.pdf} target="_blank" rel="noreferrer"><Icon icon="docs" /></a> },
                  { field: 'meter', label: strings.Meter(), render: (doc) => <Link to={`/consumption-meters/${doc._meter?._id}`}>{doc._meter?.name || doc._meter?.identifier}</Link> },
                  { field: 'billingPeriod', render: (doc) => <span>{formattedDate(doc.startDate).substring(0, 5)}→{formattedDate(doc.endDate)}</span> },
                  { field: 'consumption', label: strings.NetConsumption(), render: (doc) => <span>{Math.ceil(doc.usage)} kWh</span> },
                  { field: 'charges', render: (doc) => <span>{formatCurrency(doc.amount)}</span> },
                ]}
                query={{ utilityAccountId: _id }}
                defaultSort={{ startDate: -1 }}
              />
              :
              null
          }
          {
            activeTab === 'meters' ?
              <ListMethodCatalog
                listMethod={consumptionMeterClass.listMethod}
                columns={[
                  { field: 'name', render: (doc) => <Link to={`/consumption-meters/${doc._id}`}>{doc.name}</Link> },
                  { field: 'identifier' },
                  { field: 'accountNumber' },
                  { field: 'oneYearConsumption', label: strings.TwelveMonthUsage(), render: (doc) => <span>{typeof doc.oneYearConsumption === 'number' ? formatNumber(doc.oneYearConsumption, 0) : '—'}</span> },
                  { field: 'expiresAt', label: strings.Expires(), render: (doc) => <span>{doc.expiresAt || strings.Never()}</span> },
                  { field: 'createdAt', render: (doc) => <span>{doc.createdAt && formatDateTime(doc.createdAt)}</span> },
                ]}
                query={{ utilityAccountId: _id }}
                defaultSort={{ startDate: -1 }}
              />
              :
              null
          }
        </div>
        <div>
        </div>
      </>
    }
  />;
};